import React, { useState } from "react";
import { adminClient } from "../../../lib/admin";
import { CalendarClock, Search } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
    <input {...p} className="mt-1 w-full border border-black/15 px-2 py-1.5 text-sm" />
  </label>
);

const STATE_COLORS = {
  ACTIVE: "text-green-700", VACATION: "text-blue-600", CLOSURE_REQUESTED: "text-amber-600",
  WIND_DOWN: "text-amber-700", CLOSED: "text-gray-500", LEGAL_HOLD: "text-red-600",
  SUSPENDED: "text-red-500", UNDER_REVIEW: "text-amber-600", REACTIVATION_REQUESTED: "text-blue-500", ARCHIVED: "text-gray-400",
};

export default function AdminContinuityConsole() {
  const client = adminClient();
  const [etype, setEtype] = useState("seller");
  const [eid, setEid] = useState("");
  const [orgId, setOrgId] = useState("");
  const [rec, setRec] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const handle = (promise, ok) => promise.then(({ data }) => { toast.success(ok); lookup(); })
    .catch((e) => { const s = e?.response?.status; toast.error(s === 503 ? "Continuity engine disabled" : (e?.response?.data?.detail || "Action failed")); if (s === 503) setDisabled(true); });

  const lookup = () => {
    if (!eid) return toast.error("Entity ID required");
    client.get("/admin/continuity/state", { params: { entity_type: etype, entity_id: eid } })
      .then(({ data }) => { setRec(data); setDisabled(false); })
      .catch((e) => { if (e?.response?.status === 503) { setDisabled(true); setRec(null); } else toast.error("Lookup failed"); });
  };
  const body = () => ({ entity_type: etype, entity_id: eid });

  return (
    <div data-testid="admin-continuity-console">
      <h2 className="text-xl font-black font-display flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5" /> Business Continuity Console</h2>

      {disabled && (
        <div data-testid="continuity-disabled-banner" className="bg-taven-gray/50 border border-black/10 p-4 text-sm mb-6">
          Business Continuity engine is <b>disabled (fail-closed)</b>. Enable <code>BUSINESS_CONTINUITY_ENABLED=true</code> after a release gate to operate vacation, closure, affiliate continuity and successor workflows.
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end mb-4 border border-black/10 p-3">
        <label className="block"><span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Entity type</span>
          <select data-testid="ct-etype" value={etype} onChange={(e) => setEtype(e.target.value)} className="mt-1 block border border-black/15 px-2 py-1.5 text-sm">
            {["store", "seller", "affiliate", "creator", "instructor", "organization"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <Field label="Entity ID" data-testid="ct-eid" value={eid} onChange={(e) => setEid(e.target.value)} />
        <Field label="Org ID (for closure)" data-testid="ct-orgid" value={orgId} onChange={(e) => setOrgId(e.target.value)} />
        <button onClick={lookup} data-testid="ct-lookup-btn" className="flex items-center gap-1.5 bg-black text-white font-bold px-4 py-2 text-sm"><Search className="w-4 h-4" /> Lookup</button>
      </div>

      {rec && (
        <div data-testid="ct-state" className="border border-black/10 p-4 mb-4">
          <p className="text-sm">State: <b className={STATE_COLORS[rec.state] || ""}>{rec.state}</b></p>
          {rec.vacation && <p className="text-xs text-muted-foreground mt-1">Vacation until {rec.vacation.return_date || "—"} · new purchases {rec.vacation.pause_new_purchases ? "PAUSED" : "open"} · existing orders {rec.vacation.existing_orders_active ? "active" : "held"}</p>}
          {rec.wind_down && <p className="text-xs text-muted-foreground mt-1">Wind-down: {rec.wind_down.open_orders} open orders, {rec.wind_down.open_disputes} disputes</p>}
          {rec.final_balances && <p className="text-xs text-muted-foreground mt-1">Final balances preserved (not confiscated)</p>}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
        <div className="border border-black/10 p-3">
          <p className="font-bold mb-2">Vacation</p>
          <button data-testid="ct-vac-start" onClick={() => handle(client.post("/admin/continuity/vacation/start", { ...body(), config: { pause_new_purchases: true, message: "Away" } }), "Vacation started")} className="w-full mb-2 border border-black/15 py-1.5 hover:bg-taven-gray">Start</button>
          <button data-testid="ct-vac-end" onClick={() => handle(client.post("/admin/continuity/vacation/end", body()), "Vacation ended")} className="w-full border border-black/15 py-1.5 hover:bg-taven-gray">End / Early return</button>
        </div>
        <div className="border border-black/10 p-3">
          <p className="font-bold mb-2">Closure lifecycle</p>
          <button data-testid="ct-close-req" onClick={() => handle(client.post("/admin/continuity/closure/request", { ...body(), reason: "owner request" }), "Closure requested")} className="w-full mb-2 border border-black/15 py-1.5 hover:bg-taven-gray">Request closure</button>
          <button data-testid="ct-winddown" onClick={() => handle(client.post("/admin/continuity/closure/wind-down", { ...body(), organization_id: orgId }), "Wind-down started")} className="w-full mb-2 border border-black/15 py-1.5 hover:bg-taven-gray">Begin wind-down</button>
          <button data-testid="ct-close-complete" onClick={() => handle(client.post("/admin/continuity/closure/complete", { ...body(), organization_id: orgId }), "Closed")} className="w-full border border-black/15 py-1.5 hover:bg-taven-gray">Complete closure</button>
        </div>
        <div className="border border-black/10 p-3">
          <p className="font-bold mb-2">Governance</p>
          <button data-testid="ct-legal-hold" onClick={() => handle(client.post("/admin/continuity/legal-hold", { ...body(), reason: "review" }), "Legal hold applied")} className="w-full mb-2 border border-black/15 py-1.5 hover:bg-taven-gray text-red-600">Apply legal hold</button>
          <button data-testid="ct-reactivate" onClick={() => handle(client.post("/admin/continuity/reactivation/request", body()), "Reactivation requested")} className="w-full border border-black/15 py-1.5 hover:bg-taven-gray">Request reactivation</button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">Earned affiliate commissions are never confiscated. Sponsor reassignment requires a signed agreement + consent. Successor transfer requires signed authorization + identity verification + admin approval — never from inactivity.</p>
    </div>
  );
}
